<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="84px"
    >
      <el-form-item label="公司名称" prop="name">
        <el-input
          v-model="queryParams.name"
          class="!w-240px"
          clearable
          placeholder="请输入公司名称"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="主要联系人" prop="contactName">
        <el-input
          v-model="queryParams.contactName"
          class="!w-240px"
          clearable
          placeholder="请输入主要联系人"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="联系手机" prop="mobile">
        <el-input
          v-model="queryParams.mobile"
          class="!w-240px"
          clearable
          placeholder="请输入联系手机"
          @keyup.enter="handleQuery"
          @input="(v: string) => (queryParams.mobile = onlyDigits(v))"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <div class="mb-10px">
      <el-button
        v-hasPermi="['crm:customer:receive']"
        :disabled="selectedIds.length === 0"
        type="primary"
        @click="handleReceive(selectedIds)"
      >
        <Icon class="mr-5px" icon="ep:user-filled" />
        转为我的客户
      </el-button>
      <el-button
        v-hasPermi="['crm:customer:export']"
        :loading="exportLoading"
        plain
        type="success"
        @click="handleExport"
      >
        <Icon class="mr-5px" icon="ep:download" />
        导出
      </el-button>
    </div>

    <el-table
      v-loading="loading"
      :data="list"
      :show-overflow-tooltip="true"
      :stripe="true"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column align="center" fixed="left" label="公司名称" prop="name" width="200">
        <template #default="scope">
          <el-link :underline="false" type="primary" @click="openDetail(scope.row.id)">
            {{ scope.row.name }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="主要联系人" prop="contactName" width="110" />
      <el-table-column align="center" label="联系手机" prop="mobile" width="130" />
      <el-table-column align="center" label="微信号" prop="wechat" width="140" />
      <el-table-column align="center" label="需求分类" prop="requirementCategoryId" width="130">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_CUSTOMER_REQUIREMENT_CATEGORY"
            :value="scope.row.requirementCategoryId"
          />
        </template>
      </el-table-column>
      <el-table-column align="center" label="需求概述" prop="requirementSummary" width="200" />
      <el-table-column align="center" label="预算" prop="budget" width="130">
        <template #default="scope">{{ formatMoney(scope.row.budget) }}</template>
      </el-table-column>
      <el-table-column align="center" label="报价" prop="quotation" width="130">
        <template #default="scope">{{ formatMoney(scope.row.quotation) }}</template>
      </el-table-column>
      <el-table-column align="center" label="所在市" prop="areaName" width="180" />
      <el-table-column align="center" label="跟进记录" prop="contactLastContent" width="200" />
      <el-table-column align="center" label="申请人" prop="creatorName" width="110" />
      <el-table-column align="center" label="申请部门" prop="creatorDeptName" width="130" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="进入公海时间"
        prop="updateTime"
        width="180px"
      />
      <el-table-column
        align="center"
        fixed="right"
        :show-overflow-tooltip="false"
        label="操作"
        width="180"
      >
        <template #default="scope">
          <el-button
            v-hasPermi="['crm:customer:receive']"
            link
            type="primary"
            @click="handleReceive([scope.row.id])"
          >
            转为我的客户
          </el-button>
          <el-button link type="primary" @click="openDetail(scope.row.id)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>
</template>

<script lang="ts" setup>
import { DICT_TYPE } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import download from '@/utils/download'
import { onlyDigits } from '@/utils/inputFilter'
import * as CustomerApi from '@/api/crm/customer'

defineOptions({ name: 'CrmCustomerPool' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref<CustomerApi.CustomerVO[]>([]) // 列表的数据
const queryParams = ref<any>({
  pageNo: 1,
  pageSize: 10,
  name: '',
  contactName: '',
  mobile: '',
  sceneType: undefined,
  pool: true
})
const queryFormRef = ref() // 搜索的表单
const exportLoading = ref(false) // 导出的加载中
const selectedIds = ref<number[]>([]) // 勾选的客户编号

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await CustomerApi.getCustomerPage(queryParams.value)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.value.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.value.name = ''
  queryParams.value.contactName = ''
  queryParams.value.mobile = ''
  handleQuery()
}

/** 勾选变化 */
const handleSelectionChange = (rows: CustomerApi.CustomerVO[]) => {
  selectedIds.value = rows.map((row) => row.id)
}

/** 打开客户详情 */
const { currentRoute, push } = useRouter()
const openDetail = (id: number) => {
  push({ name: 'CrmCustomerDetail', params: { id } })
}

/** 领取公海客户（转为我的客户） */
const handleReceive = async (ids: number[]) => {
  if (!ids || ids.length === 0) {
    message.warning('请先勾选客户')
    return
  }
  try {
    await message.confirm(`确认将选中的 ${ids.length} 个客户转为我的客户吗？`)
    await CustomerApi.receiveCustomer(ids)
    message.success(t('common.updateSuccess'))
    selectedIds.value = []
    await getList()
  } catch {}
}

/** 金额展示 */
const formatMoney = (value?: number) => {
  if (value === null || value === undefined) {
    return ''
  }
  return `¥ ${Number(value).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await CustomerApi.exportCustomer(queryParams.value)
    download.excel(data, '客户公海.xls')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 监听路由变化更新列表 */
watch(
  () => currentRoute.value,
  () => {
    getList()
  }
)

/** 初始化 **/
onMounted(() => {
  getList()
})
</script>
